import { useState, useRef } from 'react';

// ── IRC & Strategy Reference Data ──────────────────────────
const tooltips: Record<string, string> = {
  // IRC Sections
  '§6166': 'IRC §6166 permits estates with closely held business interests exceeding 35% of the adjusted gross estate to defer federal estate tax payments over up to 14 years (4-year deferral + 10-year installment), with interest-only payments for the first 4 years at favorable rates.',
  '§2058': 'IRC §2058 (State Death Tax Deduction) allows estates to deduct state estate and inheritance taxes paid from the federal gross estate. This replaced the former state death tax credit under §2011. The deduction partially offsets the cost of state death taxes.',
  '§7520': 'IRC §7520 establishes the "hurdle rate" for valuing annuities, life estates, and remainders. The §7520 rate is 120% of the federal mid-term AFR, published monthly. GRATs succeed when transferred assets outperform this rate. March 2026: 4.8%.',
  '§501(c)(3)': 'IRC §501(c)(3) provides tax-exempt status for charitable organizations. Private foundations are subject to excise taxes on net investment income (§4940) and minimum distribution requirements (§4942).',
  '§2503(b)': 'IRC §2503(b) establishes the annual gift tax exclusion — $19,000 per donee for 2026. Gifts within this threshold require no Form 709 and do not consume lifetime exemption. Married couples can split gifts to $38,000/donee.',
  '§2036': 'IRC §2036 is the "retained interest" rule — if a transferor retains possession, enjoyment, or income from transferred property, the full value is pulled back into the taxable estate. Primary risk with GRATs and FLPs if not properly structured.',
  '§2702': 'IRC §2702 governs valuation of retained interests in trusts, requiring they be valued at zero unless "qualified" (annuity or unitrust). GRATs rely on the qualified annuity exception to avoid zero-valuation.',
  '§2010(c)(2)': 'IRC §2010(c)(2) provides the "anti-clawback" rule — gifts made under a higher exemption are protected even if the exemption later decreases. Gifts already made cannot be retroactively taxed.',

  // Legislation
  'TCJA': 'Tax Cuts and Jobs Act (2017) doubled the federal estate/gift exemption. Originally scheduled to sunset after 2025, but OBBBA (2025) made the higher exemption permanent at $15M/person.',
  'OBBBA': 'One Big Beautiful Bill Act, signed July 4, 2025. Permanently set the federal estate and gift tax exemption at $15M/person ($30M married). Eliminated the TCJA sunset. Indexed for inflation starting 2027.',

  // Rates
  'AFR': 'Applicable Federal Rate — minimum interest rate required by the IRS on related-party loans. Published monthly. IDGT installment sales must use at least the mid-term AFR. March 2026: 3.93% (mid-term annual).',

  // Strategy Types
  'GRAT': 'Grantor Retained Annuity Trust — grantor transfers assets and retains an annuity for a fixed term. If assets outperform the §7520 rate, the excess passes to beneficiaries gift-tax free. "Zeroed-out" GRATs set annuity equal to gift value. Primary risk: grantor must survive the term.',
  'IDGT': 'Intentionally Defective Grantor Trust — "defective" for income tax (grantor pays tax) but effective for estate tax (assets outside estate). Sale at FMV for an installment note is not taxable. Grantor paying trust income tax is an additional tax-free transfer.',
  'ILIT': 'Irrevocable Life Insurance Trust — holds life insurance outside the taxable estate. Premiums funded by annual exclusion gifts with Crummey powers. Death benefit provides estate tax liquidity. Three-year lookback rule (§2035) applies to transferred policies.',
  'Annual Gifting': 'Systematic use of the §2503(b) exclusion ($19K/donee/year, $38K if split) to transfer wealth without consuming lifetime exemption. With 12 donees, a married couple can transfer $456K annually. Over 6 years: $2.7M removed from estate.',
  'Crummey': 'Crummey withdrawal powers (from Crummey v. Commissioner) give trust beneficiaries a temporary right to withdraw gifts, converting them into "present interest" gifts that qualify for the annual exclusion. Required for gifts to irrevocable trusts.',
};

// ── Component ──────────────────────────────────────────────
export default function InfoBubble({ term }: { term: string }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLSpanElement>(null);
  const tip = tooltips[term] || `Reference: ${term}`;

  const handleEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const leftPos = Math.max(8, Math.min(rect.left - 140, window.innerWidth - 360));
      setPos({ top: rect.bottom + 6, left: leftPos });
    }
    setShow(true);
  };

  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <span
        ref={ref}
        onMouseEnter={handleEnter}
        onMouseLeave={() => setShow(false)}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 15, height: 15, borderRadius: '50%', fontSize: 9, fontWeight: 700,
          fontFamily: "'Lato', sans-serif", cursor: 'help', marginLeft: 5, flexShrink: 0,
          background: 'rgba(27,42,74,0.1)', color: '#1B2A4A',
          border: '1px solid rgba(27,42,74,0.2)', transition: 'all 0.15s',
        }}
      >
        i
      </span>
      {show && (
        <div style={{
          position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999,
          width: 340, padding: '12px 14px', borderRadius: 4,
          background: '#1B2A4A', color: '#FAF7F2',
          fontFamily: "'Libre Baskerville', Georgia, serif",
          fontSize: 12, lineHeight: 1.6,
          boxShadow: '0 8px 32px rgba(27,42,74,0.35)',
          border: '1px solid #2A3D66',
        }}>
          <div style={{
            fontFamily: "'Lato', sans-serif", fontSize: 10, fontWeight: 700,
            color: '#D4AF5C', letterSpacing: '0.08em', marginBottom: 5,
            textTransform: 'uppercase',
          }}>
            {term}
          </div>
          {tip}
        </div>
      )}
    </span>
  );
}
